import {
    Component,
    Element,
    h,
    Listen,
    Method,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

/**
 * @slot - Content shown inside the dialog
 */

@Component({
    tag: 'ap-dialog',
    styleUrl: 'ap-dialog.scss',
    shadow: true,
})
export class ApDialog {

    @Element() el: HTMLApDialogElement;

    /**
     * Show close button for dialog
     * @memberof ApDialog
     * @default true
     */
    @Prop() showCloseButton: boolean = true;

    /**
     * Top anchor for dialog
     * @memberof ApDialog
     * @default true
     */
    @Prop() top: number = 40;

    /**
     * Width of the dialog
     * @memberof ApDialog
     * @default true
     */
    @Prop() width: number = 1000;

    /**
     * Prevent closing the dialog on click outside or on ESC
     * @memberof ApDialog
     * @default false
     */
    @Prop() preventClose: boolean = false;

    /**
     * Aria label for the dialog [Required for accessibility]
     * @memberof ApDialog
     * @default 'dialog'
     */
    @Prop() dialogAriaLabel: string;

    /**
    * Theme override to bypass store state
    * @memberof ApDialog
    */
    @Prop() theme: ThemeType = 'autoTheme';

    private activeDropdownMenuSurface: HTMLPortalMenuSurfaceElement;

    /**
     * toggle dialog visibility
     * @method ApDialog
     */
    @Method()
    async toggleDialog() {
        await this.activeDropdownMenuSurface.toggleMenu();
    }

    /**
     * Open dialog
     * @method ApDialog
     */
    @Method()
    async openDialog() {
        await this.activeDropdownMenuSurface.openMenu();
    }

    /**
     * Close dialog
     * @method ApDialog
     */
    @Method()
    async closeDialog() {
        await this.activeDropdownMenuSurface.closeMenu();
    }

    /**
     * Returns true if the dialog is open
     */
    @Method()
    async isOpen() {
        return await this.activeDropdownMenuSurface.isOpen();
    }

    /**
     * Handle keyboard navigation
     * Close menu on Keyboard ESC click
     * @param ev
     */
    @Listen('keydown', { target: 'document' })
    handleKeyDown(ev: KeyboardEvent) {
        switch (ev.key) {
            case 'Escape':
                !this.preventClose && this.closeCallback();
                break;
        }
    }

    render() {
        return (
            <portal-menu-surface
                slideTransition={false}
                backgroundEffect={true}
                data-test="ap-dialog"
                data-cy="ap-dialog"
                theme={this.theme}
                center
                top={`${this.top}%`}
                ref={ (elem: HTMLPortalMenuSurfaceElement) => this.activeDropdownMenuSurface = elem }
                preventClose={this.preventClose}
                role="dialog"
                aria-modal="true"
                aria-label={this.dialogAriaLabel}
            >
                <div class="dialog" data-test="ap-dialog" style={{ width: `${this.width}px` }}>
                    <div class="dialog-content" data-test="ap-dialog-content">
                        <slot></slot>
                    </div>
                    {this.showCloseButton &&
                    <div class="dialog-close" data-test="ap-dialog-close-button">
                        <portal-icon
                            name="close"
                            class="dialog-close-button"
                            onClick={() => this.closeCallback()}
                        />
                    </div>}
                </div>
            </portal-menu-surface>
        );
    }

    private closeCallback() {
        this.activeDropdownMenuSurface.closeMenu();
    }
}
